import { motion } from 'framer-motion'
import clsx from 'clsx'
import { ForecastHistoryItem } from '../../utils/api'
import { formatTimeAgo } from '../../utils/format'
import { CategoryBadge, StatusBadge } from './ForecastBadges'

interface ForecastCardProps {
  forecast: ForecastHistoryItem
  isActive: boolean
  onClick: () => void
  onDelete?: () => void
}

function getVerdictColor(probability: number) {
  if (probability >= 0.7) return 'border-l-signal-green'
  if (probability <= 0.3) return 'border-l-signal-red'
  return 'border-l-signal-amber'
}

export function ForecastCard({
  forecast,
  isActive,
  onClick,
  onDelete,
}: ForecastCardProps) {
  const hasResult = forecast.status === 'completed' && forecast.probability !== null
  const isRunning = forecast.status === 'running' || forecast.status === 'analyzing'

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      onClick={onClick}
      className={clsx(
        'glass-card p-3 cursor-pointer transition-all border-l-4',
        isActive && 'ring-2 ring-signal-blue/50',
        'hover:bg-void-50/50',
        hasResult ? getVerdictColor(forecast.probability!) : 'border-l-transparent',
        isRunning && 'border-l-signal-blue'
      )}
    >
      {/* Top row: Question + Actions */}
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-star line-clamp-2 flex-1">{forecast.question}</p>
        {onDelete && (forecast.status === 'completed' || forecast.status === 'failed') && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="flex-shrink-0 p-2 -m-1 min-h-[44px] min-w-[44px] flex items-center justify-center rounded text-star-dim/50 hover:text-signal-red hover:bg-signal-red/10 transition-all"
            title="Delete forecast"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>

      {/* Bottom row: Metadata + Probability */}
      <div className="flex items-center justify-between gap-2 mt-2">
        <div className="flex items-center gap-2 min-w-0">
          <CategoryBadge category={forecast.category} size="sm" />
          <span className="text-xs text-star-dim truncate">
            {forecast.started_at ? formatTimeAgo(forecast.started_at) : 'Just now'}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {hasResult ? (
            <span className={clsx(
              'font-mono text-sm font-semibold',
              forecast.probability! >= 0.7 ? 'text-signal-green' :
              forecast.probability! <= 0.3 ? 'text-signal-red' :
              'text-signal-amber'
            )}>
              {(forecast.probability! * 100).toFixed(0)}%
            </span>
          ) : (
            <StatusBadge status={forecast.status} />
          )}
        </div>
      </div>

      {/* Progress bar for running forecasts */}
      {isRunning && (forecast as ForecastHistoryItem & { progress?: number }).progress !== undefined && (
        <div className="mt-2 h-1 bg-star-dim/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-signal-blue rounded-full transition-all duration-500"
            style={{ width: `${(forecast as ForecastHistoryItem & { progress?: number }).progress}%` }}
          />
        </div>
      )}
    </motion.div>
  )
}
